// Locate Me overlay button. Lives inside a <MapContainer> so it can use
// `useMap()` for imperative flyTo. Kicks off the shared useLiveTracking
// hook via callbacks passed by the parent.
//
// `position` chooses which corner the button sits in relative to its
// nearest positioned ancestor (the leaflet-container). Default matches
// the original MapPage placement so its layout is unchanged.
import { useMap } from "react-leaflet";
import { toast } from "sonner";
import { Locate } from "lucide-react";
import { readCachedPos } from "../lib/useLiveTracking";

const POSITION_CLASSES = {
  "bottom-right": "bottom-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "top-right": "top-4 right-4",
  "top-left": "top-4 left-4",
};

export default function LocateBtn({ onLocated, tracking, onStartTracking, busy, setBusy, position = "bottom-right", isFollowing = false, setIsFollowing }) {
  const map = useMap();
  const engageFollow = () => { if (setIsFollowing) setIsFollowing(true); };
  const onClick = () => {
    if (busy) return;
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location lookup.");
      return;
    }
    if (tracking) {
      // Already tracking — this is a re-engage. Flip the follow lock ON
      // and pan to the last known coordinate immediately.
      engageFollow();
      const cached = readCachedPos();
      if (cached) map.flyTo(cached.position, Math.max(map.getZoom(), 15), { duration: 0.6 });
      return;
    }
    const cached = readCachedPos();
    if (cached) {
      map.flyTo(cached.position, Math.max(map.getZoom(), 13), { duration: 0.8, easeLinearity: 0.25 });
      onLocated(cached);
    } else {
      setBusy(true);
    }
    engageFollow();
    onStartTracking({
      onFirstFix: (payload) => {
        const drifted =
          !cached ||
          Math.hypot((cached.position[0] - payload.position[0]) * 111000, (cached.position[1] - payload.position[1]) * 111000 * Math.cos(payload.position[0] * Math.PI / 180)) > 20;
        if (drifted) {
          map.flyTo(payload.position, Math.max(map.getZoom(), 13), { duration: cached ? 0.6 : 1.2, easeLinearity: 0.25 });
        }
        setBusy(false);
      },
      onError: (err) => {
        setBusy(false);
        if (cached) return;
        if (err && err.code === 1) {
          toast.error("We need permission to access your location to show you on the map.");
        } else if (err && err.code === 3) {
          toast.error("Couldn't pin your location — try again in a moment.");
        } else {
          toast.error("Couldn't get your location.");
        }
      },
    });
  };
  // Active styling: tracking = ON with follow-lock engaged. If tracking
  // is on but the walker has manually panned away (isFollowing = false),
  // fall back to the neutral pill so the button visibly invites a
  // re-engage tap.
  const active = tracking && isFollowing;
  return (
    <button
      data-testid="locate-me-btn"
      onClick={onClick}
      disabled={busy}
      className={`absolute ${POSITION_CLASSES[position] || POSITION_CLASSES["bottom-right"]} z-[550] w-12 h-12 min-w-[48px] min-h-[48px] rounded-full border shadow-lg flex items-center justify-center active:scale-95 transition disabled:opacity-60 ${
        active
          ? "bg-brand-primary text-brand-sand border-brand-primary hover:bg-brand-primary/90"
          : "bg-brand-surface border-brand-border hover:bg-brand-sand"
      }`}
      aria-label={active ? "Following your location" : tracking ? "Re-centre and follow me" : "Locate me"}
      aria-pressed={active}
      data-following={active ? "true" : "false"}
      type="button"
    >
      <Locate size={20} className={`${active ? "text-brand-sand" : "text-brand-primary"} ${busy ? "animate-pulse" : ""}`} />
    </button>
  );
}
